import { isEqual } from 'lodash-es';
import { createWithEqualityFn } from 'zustand/traditional';

import { ApplicationType, ApplicationStatus, ApplyingCollegeWithSchool } from '@common-types';

import { INITIAL_PAGE_FILTERS } from '@constants/applyingSchoolsPlanner';
import { ORDER_BY_EARLIEST } from '@constants';

import { baseService } from '@services/base.service';
import useNotificationStore, { notify } from '@store/NotificationStore';

export interface ApplyingSchoolsFilters {
  applicationType: ApplicationType | string;
  applicationStatus: ApplicationStatus | string;
}

interface ApplyingSchoolsPlannerStore {
  appliedSchools: ApplyingCollegeWithSchool[];
  pageFilters: ApplyingSchoolsFilters;
  orderBy: string;

  getAppliedSchool: () => Promise<void>;
  deleteAppliedSchool: (schoolId: number) => Promise<void>;
  editAppliedSchool: (data: Partial<ApplyingCollegeWithSchool>, schoolName: string) => Promise<void>;

  setPageFilters: (filters: Partial<ApplyingSchoolsFilters>) => void;
  setOrderBy: (order: string) => void;
}

const initialState = {
  appliedSchools: [],
  pageFilters: INITIAL_PAGE_FILTERS,
  orderBy: ORDER_BY_EARLIEST,
};

const schoolKey = (school: ApplyingCollegeWithSchool) => `${school.id ?? ''}:${school.memberId ?? ''}`;

/**
 * A refresh must not change the object identity of schools whose data did not change.
 * PlannerSchoolCard resets its form from a `[applyingSchool]` effect, so a wholesale
 * array swap would discard a card the user is part-way through editing.
 */
const reuseUnchanged = (
  current: ApplyingCollegeWithSchool[],
  fetched: ApplyingCollegeWithSchool[],
): ApplyingCollegeWithSchool[] => {
  const byKey = new Map(current.map((school) => [schoolKey(school), school]));

  return fetched.map((school) => {
    const existing = byKey.get(schoolKey(school));

    return existing && isEqual(existing, school) ? existing : school;
  });
};

export const useApplyingSchoolsPlannerStore = createWithEqualityFn<ApplyingSchoolsPlannerStore>()(
  (set, get) => ({
    ...initialState,

    getAppliedSchool: async () => {
      const fetched = await baseService.getAsync<ApplyingCollegeWithSchool[]>('/user/applying-college');

      set({
        appliedSchools: reuseUnchanged(get().appliedSchools, fetched),
      });
    },

    editAppliedSchool: async (applyingCollegeWithSchool: Partial<ApplyingCollegeWithSchool>, schoolName) => {
      const filterFalsyValues = (obj: Record<string, unknown> | undefined) => {
        if (!obj) return {};

        const newObj = { ...obj };

        Object.keys(newObj).forEach((key) => !newObj[key] && delete newObj[key]);

        return newObj;
      };

      const applicationDataWithoutFalsyValues = filterFalsyValues(applyingCollegeWithSchool.application);
      const dataWithoutFalsyValues = filterFalsyValues(applyingCollegeWithSchool);

      await baseService.patchAsync(`/user/applying-college`, {
        ...dataWithoutFalsyValues,
        application: applicationDataWithoutFalsyValues,
      });

      get().getAppliedSchool();

      notify({
        title: `Changes to the application information for ${schoolName} saved.`,
        message: '',
        severity: 'success',
      });
    },

    deleteAppliedSchool: async (schoolId: number) => {
      const { appliedSchools } = get();

      await baseService.deleteAsync(`/user/applying-college/${schoolId}`);

      const filteredSchools = appliedSchools.filter((school) => school.id !== schoolId);

      set({
        appliedSchools: filteredSchools,
      });

      useNotificationStore.getState().notify({
        title: 'School was removed.',
        message: 'You can add it back to your list.',
        severity: 'success',
      });
    },

    setPageFilters: (filters: Partial<ApplyingSchoolsFilters>) => {
      set({
        pageFilters: { ...get().pageFilters, ...filters },
      });
    },

    setOrderBy: (orderBy: string) => {
      set({
        orderBy,
      });
    },
  }),
  Object.is,
);
